import type { StoreDto } from '@cartly/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
  Spinner,
  cx,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { ShoppingBag, ShoppingCart } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import type { CheckoutRow, OrderDetail, OrderRow, Page } from '../../api/types';
import { LoadMore, StatusBadge } from '../../components/Common';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatRelative,
  personName,
} from '../../app/format';
import { useLoader, usePaged } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

const COD_LABEL = {
  pending: 'COD: waiting for reply',
  confirmed: 'COD: confirmed',
  declined: 'COD: declined',
} as const;

function CodBadge({ status }: { status: 'pending' | 'confirmed' | 'declined' }) {
  const tone = status === 'confirmed' ? 'success' : status === 'declined' ? 'critical' : 'warning';
  return <Badge tone={tone}>{COD_LABEL[status]}</Badge>;
}

function OrderModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, error, loading, reload } = useLoader(
    () => api.get<OrderDetail>(`/orders/${id}`),
    id,
  );
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);
  const storeAction = async (kind: 'mark-paid' | 'cancel') => {
    if (kind === 'cancel') {
      const ok = await confirm({
        title: 'Cancel in Shopify',
        message:
          'This cancels the order in your Shopify store, restocks the items and does not refund or notify the customer. It cannot be undone.',
        confirmLabel: 'Cancel order',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusy(kind);
    try {
      await api.post(`/orders/${id}/shopify/${kind}`, {});
      toast.success(
        kind === 'cancel' ? 'Cancellation sent to Shopify' : 'Marked as paid in Shopify',
      );
      // Shopify confirms with a webhook; reload shows the result once it arrives.
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };
  const timeline = data
    ? (
        [
          ['Placed', data.placedAt],
          ['Paid', data.paidAt],
          ['Fulfilled', data.fulfilledAt],
          ['Delivered', data.deliveredAt],
          ['Cancelled', data.cancelledAt],
          ['Archived', data.closedAt],
          ['Deleted in store', data.deletedAt],
        ] as const
      ).filter(([, at]) => at)
    : [];
  return (
    <Modal
      open
      title={data ? `Order ${data.orderNumber ?? data.externalId}` : 'Order'}
      onClose={onClose}
    >
      {loading && !data ? (
        <Spinner />
      ) : error || !data ? (
        <Alert tone="critical">{error}</Alert>
      ) : (
        <div className="stack">
          {data.deletedAt && (
            <Alert tone="warning" title="Deleted in the store">
              This order no longer exists in the store. It is kept here as history and is not
              counted in totals.
            </Alert>
          )}
          <div className="row">
            {data.deletedAt && <Badge tone="critical">Deleted</Badge>}
            {!data.deletedAt && data.cancelledAt && <Badge tone="critical">Cancelled</Badge>}
            {!data.deletedAt && !data.cancelledAt && data.closedAt && (
              <Badge tone="neutral">Archived</Badge>
            )}
            <StatusBadge status={data.financialStatus} />
            <StatusBadge status={data.fulfillmentStatus} />
            {data.paymentMethod === 'cod' && <Badge tone="info">Cash on delivery</Badge>}
            {data.recoveredFrom && (
              <Badge tone="primary">Recovered {data.recoveredFrom.kind}</Badge>
            )}
            {data.cod.status && <CodBadge status={data.cod.status} />}
            {data.paymentLink?.paidAt && <Badge tone="success">Paid via link</Badge>}
          </div>
          {data.risk && data.paymentMethod === 'cod' && (
            <Alert
              tone={
                data.risk.level === 'high'
                  ? 'critical'
                  : data.risk.level === 'medium'
                    ? 'warning'
                    : 'info'
              }
              title={`Delivery risk: ${data.risk.level} (${data.risk.score}/100)`}
            >
              <ul>
                {data.risk.reasons.map((r) => (
                  <li key={r.code}>
                    {r.label} ({r.points > 0 ? '+' : ''}
                    {r.points})
                  </li>
                ))}
              </ul>
              <p className="text-muted text-sm">
                A rule-based estimate from this order and the customer’s history, not a prediction
                trained on your data.
              </p>
            </Alert>
          )}
          {data.paymentLink && (
            <Alert tone={data.paymentLink.paidAt ? 'success' : 'info'} title="Payment link">
              {data.paymentLink.paidAt
                ? `Paid online on ${formatDateTime(data.paymentLink.paidAt)}. ${
                    data.paidAt
                      ? 'The order is marked paid in your store.'
                      : 'Cartly marks the order as paid in Shopify automatically; if that did not happen, use “Mark paid in Shopify” above so it is not collected again on delivery.'
                  }`
                : 'Sent to the customer, not paid yet.'}{' '}
              <span className="mono text-sm">{data.paymentLink.url}</span>
            </Alert>
          )}
          {can('orders:manage') && data.writeBack.reason !== 'not_shopify' && (
            <div className="stack">
              {data.writeBack.reason === 'missing_scope' && (
                <Alert tone="warning" title="Shopify permission needed">
                  Cartly cannot change orders in your store yet. Approve the updated permissions for
                  the Cartly360 app in your Shopify admin, then try again.
                </Alert>
              )}
              {data.writeBack.error && (
                <Alert tone="critical" title="Last store update failed">
                  {data.writeBack.error}
                </Alert>
              )}
              <div className="row">
                {!data.paidAt && !data.cancelledAt && !data.deletedAt && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={busy === 'mark-paid'}
                    disabled={!data.writeBack.available}
                    onClick={() => void storeAction('mark-paid')}
                  >
                    Mark paid in Shopify
                  </Button>
                )}
                {!data.paidAt && !data.cancelledAt && !data.deletedAt && !data.fulfilledAt && (
                  <Button
                    size="sm"
                    variant="danger"
                    loading={busy === 'cancel'}
                    disabled={!data.writeBack.available}
                    onClick={() => void storeAction('cancel')}
                  >
                    Cancel in Shopify
                  </Button>
                )}
              </div>
            </div>
          )}
          <dl className="dl">
            <dt>Customer</dt>
            <dd>
              {personName(data.customer)}
              {data.customer?.email && (
                <div className="text-muted text-sm">{data.customer.email}</div>
              )}
              {data.customer?.phone && (
                <div className="text-muted text-sm">{data.customer.phone}</div>
              )}
            </dd>
            <dt>Store</dt>
            <dd>{data.store.name}</dd>
          </dl>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th className="table__actions">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <div className="table__primary">{i.title}</div>
                      {i.sku && <div className="table__secondary mono">{i.sku}</div>}
                    </td>
                    <td>{i.quantity}</td>
                    <td className="table__actions">{formatMoney(i.total, data.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <dl className="dl">
            <dt>Total</dt>
            <dd className="text-strong">{formatMoney(data.total, data.currency)}</dd>
            {data.refunds.map((r) => (
              <div key={r.id} className="dl-row">
                <dt>Refund {formatDate(r.refundedAt)}</dt>
                <dd>
                  −{formatMoney(r.amount, data.currency)}{' '}
                  {r.reason && <span className="text-muted">({r.reason})</span>}
                </dd>
              </div>
            ))}
          </dl>
          <ol className="timeline">
            {timeline.map(([label, at]) => (
              <li key={label} className="timeline__item">
                <span className="timeline__label">{label}</span>
                <span className="text-muted text-sm">{formatDateTime(at!)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Modal>
  );
}

function OrdersTable({
  storeId,
  payment,
  state,
}: {
  storeId: string;
  payment: string;
  state: string;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const q = new URLSearchParams({
    limit: '50',
    ...(storeId && { storeId }),
    ...(payment && { paymentMethod: payment }),
    ...(state && { state }),
  });
  const list = usePaged(
    (cursor) => api.get<Page<OrderRow>>(`/orders?${q}${cursor ? `&cursor=${cursor}` : ''}`),
    `${storeId}:${payment}:${state}`,
  );
  if (list.loading && list.items.length === 0)
    return (
      <div className="card__body">
        <Spinner />
      </div>
    );
  if (list.error)
    return (
      <div className="card__body">
        <Alert tone="critical">{list.error}</Alert>
      </div>
    );
  if (list.items.length === 0) {
    return (
      <EmptyState icon={<ShoppingBag size={22} />} title="No orders yet">
        Orders appear here as soon as a connected store sends <code>order.created</code> events.
      </EmptyState>
    );
  }
  return (
    <>
      <div className="table-wrap">
        <table className="table table--clickable">
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Payment</th>
              <th>Fulfillment</th>
              <th>Placed</th>
              <th className="table__actions">Total</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((o) => (
              <tr
                key={o.id}
                onClick={() => setOpen(o.id)}
                tabIndex={0}
                onKeyDown={(k) => k.key === 'Enter' && setOpen(o.id)}
              >
                <td>
                  <div className="table__primary">{o.orderNumber ?? o.externalId}</div>
                  <div className="table__secondary">{o.store.name}</div>
                </td>
                <td>{personName(o.customer)}</td>
                <td>
                  <div className="row">
                    {o.deletedAt ? (
                      <Badge tone="critical">Deleted</Badge>
                    ) : (
                      <StatusBadge
                        status={o.cancelledAt ? 'voided' : o.financialStatus}
                        label={o.cancelledAt ? 'cancelled' : undefined}
                      />
                    )}
                    {!o.deletedAt && !o.cancelledAt && o.closedAt && (
                      <Badge tone="neutral">Archived</Badge>
                    )}
                    {o.paymentMethod === 'cod' && <Badge tone="info">COD</Badge>}
                    {o.codStatus && <CodBadge status={o.codStatus} />}
                  </div>
                </td>
                <td>
                  <StatusBadge status={o.fulfillmentStatus} />
                </td>
                <td className="text-muted text-sm">{formatDate(o.placedAt)}</td>
                <td className="table__actions">{formatMoney(o.total, o.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {list.hasMore && <LoadMore onClick={() => void list.loadMore()} loading={list.loadingMore} />}
      {open && <OrderModal id={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function CartsTable({ storeId, status }: { storeId: string; status: string }) {
  const q = new URLSearchParams({
    limit: '50',
    ...(storeId && { storeId }),
    ...(status && { status }),
  });
  const list = usePaged(
    (cursor) => api.get<Page<CheckoutRow>>(`/checkouts?${q}${cursor ? `&cursor=${cursor}` : ''}`),
    `${storeId}:${status}`,
  );
  if (list.loading && list.items.length === 0)
    return (
      <div className="card__body">
        <Spinner />
      </div>
    );
  if (list.error)
    return (
      <div className="card__body">
        <Alert tone="critical">{list.error}</Alert>
      </div>
    );
  if (list.items.length === 0) {
    return (
      <EmptyState icon={<ShoppingCart size={22} />} title="No carts here">
        Abandoned carts and checkouts appear when stores send <code>cart.abandoned</code> or{' '}
        <code>checkout.abandoned</code>. Recovery messaging arrives with WhatsApp automation.
      </EmptyState>
    );
  }
  return (
    <>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Type</th>
              <th>Status</th>
              <th>Items</th>
              <th>Last activity</th>
              <th className="table__actions">Value</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((c) => (
              <tr key={c.id}>
                <td>
                  <div className="table__primary">{personName(c.customer)}</div>
                  <div className="table__secondary">{c.customer?.email ?? c.store.name}</div>
                </td>
                <td className="text-sm">{c.kind === 'cart' ? 'Cart' : 'Checkout'}</td>
                <td>
                  <StatusBadge status={c.status} />
                </td>
                <td>{c.itemCount}</td>
                <td className="text-muted text-sm">
                  {formatRelative(c.abandonedAt ?? c.lastActivityAt)}
                </td>
                <td className="table__actions">{formatMoney(c.total, c.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {list.hasMore && <LoadMore onClick={() => void list.loadMore()} loading={list.loadingMore} />}
    </>
  );
}

export function OrdersPage() {
  const session = useActiveSession();
  const [view, setView] = useState<'orders' | 'carts'>('orders');
  const [storeId, setStoreId] = useState('');
  const [payment, setPayment] = useState('');
  const [orderState, setOrderState] = useState('');
  const [cartStatus, setCartStatus] = useState('abandoned');
  const stores = useLoader(
    () => api.get<{ items: StoreDto[] }>('/stores').then((r) => r.items),
    session.activeTenant?.id,
  );

  return (
    <>
      <PageHeader
        title="Orders"
        description="Orders and carts synchronized from every connected store."
      />
      <nav className="tabs settings-tabs" aria-label="Order views">
        {(
          [
            ['orders', 'Orders'],
            ['carts', 'Carts & checkouts'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={cx('tabs__tab tabs__tab--button', view === id && 'tabs__tab--active')}
            aria-current={view === id ? 'page' : undefined}
            onClick={() => setView(id)}
          >
            {label}
          </button>
        ))}
      </nav>
      <Card
        flush
        title={view === 'orders' ? 'Orders' : 'Carts & checkouts'}
        actions={
          <div className="row">
            <select
              className="select select--inline"
              aria-label="Store"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
            >
              <option value="">All stores</option>
              {(stores.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {view === 'orders' ? (
              <>
                <select
                  className="select select--inline"
                  aria-label="Order state"
                  value={orderState}
                  onChange={(e) => setOrderState(e.target.value)}
                >
                  <option value="">All orders</option>
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="archived">Archived</option>
                  <option value="deleted">Deleted</option>
                </select>
                <select
                  className="select select--inline"
                  aria-label="Payment method"
                  value={payment}
                  onChange={(e) => setPayment(e.target.value)}
                >
                  <option value="">All payments</option>
                  <option value="cod">Cash on delivery</option>
                  <option value="prepaid">Prepaid</option>
                </select>
              </>
            ) : (
              <select
                className="select select--inline"
                aria-label="Cart status"
                value={cartStatus}
                onChange={(e) => setCartStatus(e.target.value)}
              >
                <option value="abandoned">Abandoned</option>
                <option value="recovered">Recovered</option>
                <option value="open">Open</option>
                <option value="completed">Completed</option>
              </select>
            )}
          </div>
        }
      >
        {view === 'orders' ? (
          <OrdersTable storeId={storeId} payment={payment} state={orderState} />
        ) : (
          <CartsTable storeId={storeId} status={cartStatus} />
        )}
      </Card>
    </>
  );
}
